import { HttpErrorResponse } from '@angular/common/http';
import { Component, ContentChild, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TableService } from '../../../services/table.service';
import { GenericQueryParams, TableHeadersConfig } from '../../../utils/utils';
import { GenericPaginatedDataType } from '../../../services/common.service';

type QueryFn<T> = (params: GenericQueryParams) => Observable<GenericPaginatedDataType<T>>;

@Component({
  selector: 'app-table',
  templateUrl: 'table.component.html',
})
export class TableComponent<T> implements OnInit, OnDestroy {
  @Input() tableName: string;
  @Input() headers: TableHeadersConfig[];
  @Input() dataObservable: QueryFn<T>;

  @ContentChild('tableRow') tableRowTemplateRef: TemplateRef<any>;
  @ContentChild('addContent') addContentRef: TemplateRef<any>;
  @ContentChild('titleContent') titleContentRef: TemplateRef<any>;

  public isLoading: boolean;
  public data: T[];
  private searchSubscription: Subscription;
  private dataSubscription: Subscription;
  private triggerSubscription: Subscription;

  private itemsPerPage = 10;
  private currentPage = 0;
  public totalPages: number;
  private pagesMap = new Map<number, T[]>();
  private queryParams: GenericQueryParams = {
    pagination: {
      pageNumber: '0',
    },
  };

  private searchSubject = new Subject<string>();

  constructor(public tableService: TableService) {}

  ngOnInit(): void {
    this.getData();
    this.searchSubscription = this.searchSubject.pipe(debounceTime(300)).subscribe((searchValue) => {
      if (searchValue)
        this.queryParams.search = {
          search: searchValue,
        };
      this.getData(true);
    });
    this.triggerSubscription = this.tableService.getQuerySubject().subscribe(() => this.getData(true));
  }

  getData(refresh: boolean = false) {
    const currentData = this.pagesMap.get(this.currentPage);
    if (currentData && !refresh) {
      this.data = currentData;
    } else {
      this.isLoading = true;
      this.data = [];
      this.dataSubscription = this.dataObservable(this.queryParams).subscribe(
        ({ elements, pages, values }) => {
          this.isLoading = false;
          this.totalPages = pages;
          this.data = values;
          if (refresh) {
            this.pagesMap.clear();
          }
          this.pagesMap.set(this.currentPage, values);
        },
        (err: HttpErrorResponse) => { this.isLoading = false; }
      );
    }
  }

  onPageChange(event: number) {
    this.currentPage = event;
    this.queryParams.pagination.pageNumber = event.toString();
    this.getData();
  }

  onTableHeaderClick(header: TableHeadersConfig) {
    if (!header.sortingKey) return;

    if (!this.queryParams.sorting) {
      this.queryParams.sorting = {
        orderBy: header.sortingKey,
        order: 'asc',
      };
      this.getData(true);
      return;
    }

    if (header.sortingKey !== this.queryParams.sorting.orderBy) {
      this.queryParams.sorting = {
        orderBy: header.sortingKey,
        order: 'asc',
      };
    } else {
      this.queryParams.sorting = {
        orderBy: header.sortingKey,
        order: this.queryParams.sorting.order === 'asc' ? 'desc' : 'asc',
      };
    }
    this.getData(true);
  }
  onSearch(search: string) {
    if (!search) {
      delete this.queryParams.search;
    }
    this.searchSubject.next(search);
  }
  getElementIndex(index: number) {
    return this.currentPage * this.itemsPerPage + (index + 1);
  }
  ngOnDestroy(): void {
    if (this.searchSubscription) this.searchSubscription.unsubscribe();
    if (this.dataSubscription) this.dataSubscription.unsubscribe();
    this.triggerSubscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./src/app/components/table/table.component.ts